/**
 * lib/cards/shared.js — Common helpers for card templates.
 *
 * Extracted from the original card-export.js (Apr 23 2026 rewrite) so
 * Minimal and Premium templates can share identical image loading,
 * name privacy rules, QR generation, and colour parsing.
 *
 * NOTHING in here should be template-specific. If a helper starts
 * having a 'template' param, it belongs in the template file instead.
 *
 * Exports:
 *   generateQR(text, size)          — QR as data URL
 *   verifyUrl(tenantSlug, staffSlug) — full public verify URL
 *   applyDisplayNameMode(f, l, mode) — "Dani Morgan" / "Dani M." / "Dani"
 *   loadImageAsDataUrl(url)         — CORS-safe <img> + canvas loader
 *   deriveInitials(first, last)     — up-to-2-char uppercase initials
 *   parseHexColor(hex)              — { r, g, b } or null
 *   stripProtocol(url)              — drop https:// and trailing slash
 *   drawPhotoPlaceholder(pdf, ...)  — cream square with initials
 *   premultiplyAlpha(colour, alpha) — fake alpha blending against white
 */

import QRCode from 'qrcode';

/* ─────────────────────────────────────────────────────────────── */
/*  QR                                                              */
/* ─────────────────────────────────────────────────────────────── */

/**
 * Generate a QR code as a data URL PNG.
 * Error correction H (30% damage tolerance) — essential for printed
 * cards that will get scuffed in pockets and lanyards.
 */
export async function generateQR(text, size = 512) {
  return QRCode.toDataURL(text, {
    errorCorrectionLevel: 'H',
    margin: 2,
    width: size,
    color: {
      dark: '#000000',
      light: '#FFFFFF',
    },
  });
}

/* ─────────────────────────────────────────────────────────────── */
/*  URLs                                                            */
/* ─────────────────────────────────────────────────────────────── */

/**
 * Build the public verify URL for a given tenant + staff slug.
 * Uses VITE_APP_BASE_URL, falls back to window origin.
 */
export function verifyUrl(tenantSlug, staffSlug) {
  const base = import.meta.env.VITE_APP_BASE_URL || window.location.origin;
  const clean = base.endsWith('/') ? base.slice(0, -1) : base;
  return `${clean}/v/${encodeURIComponent(tenantSlug)}/${encodeURIComponent(staffSlug)}`;
}

/**
 * Drop protocol and trailing slash from a URL for display.
 * "https://coastalcleaning.org.uk/" → "coastalcleaning.org.uk"
 */
export function stripProtocol(url) {
  if (!url) return '';
  return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

/* ─────────────────────────────────────────────────────────────── */
/*  Name privacy                                                    */
/* ─────────────────────────────────────────────────────────────── */

/**
 * Apply tenant's display_name_mode to a staff name. Returns a string.
 *
 * Same logic is used in the public verify page and Branding preview
 * — keep in sync if this ever changes.
 *
 * Enum values: 'full' | 'initial' | 'first_only'
 * (NOT 'first_last_initial' — that was the pre-Apr-23 bug. See memory.)
 */
export function applyDisplayNameMode(firstName, lastName, mode) {
  const first = (firstName || '').trim();
  const last = (lastName || '').trim();

  if (!first && !last) return '';
  if (!last) return first;

  switch (mode) {
    case 'initial':
      return `${first} ${last.charAt(0).toUpperCase()}.`;
    case 'first_only':
      return first;
    case 'full':
    default:
      return `${first} ${last}`;
  }
}

/* ─────────────────────────────────────────────────────────────── */
/*  Image loading                                                   */
/* ─────────────────────────────────────────────────────────────── */

/**
 * Load an image URL and return a data URL + natural dimensions.
 *
 * Uses <img crossOrigin="anonymous"> + canvas.toDataURL. Requires the
 * image server to send Access-Control-Allow-Origin — the TocynID
 * /media/* route does as of Apr 23 2026 (see RANDY's Apr 23 infra
 * update).
 *
 * If you see "Tainted canvas" in the console: CORS is missing on the
 * media route. Don't try to work around it here — fix the server.
 */
export function loadImageAsDataUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        // PNG preserves transparency for logos on light cards.
        const dataUrl = canvas.toDataURL('image/png');
        resolve({
          dataUrl,
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => reject(new Error(`Image load failed: ${url}`));
    img.src = url;
  });
}

/* ─────────────────────────────────────────────────────────────── */
/*  Initials / placeholder                                          */
/* ─────────────────────────────────────────────────────────────── */

/**
 * Derive up-to-2-character initials, uppercased. Handles missing
 * parts by using whatever's available. Returns '?' if empty.
 */
export function deriveInitials(firstName, lastName) {
  const first = (firstName || '').trim();
  const last = (lastName || '').trim();
  const fi = first.charAt(0).toUpperCase();
  const li = last.charAt(0).toUpperCase();
  const combined = `${fi}${li}`;
  return combined || '?';
}

/**
 * Draw a photo placeholder rectangle with initials centred.
 * Used as fallback when photo URL fails to load or is missing.
 */
export function drawPhotoPlaceholder(pdf, x, y, w, h, initials, options = {}) {
  const bg = options.backgroundColor || { r: 244, g: 233, b: 214 }; // cream default
  const fg = options.textColor || { r: 107, g: 122, b: 112 };       // muted green-grey
  const fontSize = options.fontSize || 18;

  pdf.setFillColor(bg.r, bg.g, bg.b);
  pdf.rect(x, y, w, h, 'F');

  pdf.setFontSize(fontSize);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(fg.r, fg.g, fg.b);

  // +2mm vertical offset approximates baseline centring for helvetica
  pdf.text(
    (initials || '?').toUpperCase(),
    x + w / 2,
    y + h / 2 + 2,
    { align: 'center' },
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Colour                                                          */
/* ─────────────────────────────────────────────────────────────── */

/**
 * Parse #RRGGBB hex into { r, g, b } 0-255 ints. Returns null on
 * invalid input. Does NOT support #RGB shorthand — that's rare in
 * brand tokens and skipping it keeps validation strict.
 */
export function parseHexColor(hex) {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

/**
 * Premultiply a colour against white for a given alpha. jsPDF's free
 * build doesn't support per-path opacity, so drawing at a chosen
 * alpha against a white card background is faked this way.
 *
 * At alpha=0.2 against white: the colour renders 80% lighter.
 */
export function premultiplyAlpha({ r, g, b }, alpha) {
  return {
    r: Math.round(r * alpha + 255 * (1 - alpha)),
    g: Math.round(g * alpha + 255 * (1 - alpha)),
    b: Math.round(b * alpha + 255 * (1 - alpha)),
  };
}

/**
 * Determine if a background colour needs dark or light text for
 * legibility. Uses relative luminance (WCAG formula).
 * Returns 'dark' or 'light' — i.e. the colour the text should be.
 */
export function contrastingTextColor({ r, g, b }) {
  // WCAG relative luminance (simplified — gamma ignored; good enough for print)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? 'dark' : 'light';
}

/* ─────────────────────────────────────────────────────────────── */
/*  Brand colour defaults                                           */
/* ─────────────────────────────────────────────────────────────── */

/**
 * Tocyn green fallback. Used when tenant.brand_color is missing or
 * invalid.
 */
export const DEFAULT_BRAND_COLOR = { r: 0, g: 171, b: 57 }; // #00AB39

/**
 * Resolve a hex to { r, g, b } with fallback. Convenience wrapper
 * around parseHexColor that never returns null.
 */
export function resolveBrandColor(hex) {
  return parseHexColor(hex) || DEFAULT_BRAND_COLOR;
}

/* ─────────────────────────────────────────────────────────────── */
/*  Card dimension constants                                        */
/* ─────────────────────────────────────────────────────────────── */

/**
 * ISO/IEC 7810 ID-1 standard credit-card size. Shared by both
 * templates so printers can use the same PVC stock for either.
 */
export const CR80 = {
  widthMm: 85.6,
  heightMm: 53.98,
};
